import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  StorageLocationQuery,
  CreateStorageLocationInput,
  UpdateStorageLocationInput,
} from '@maker-toolchest/shared';
import { storageApi } from './api';

const QUERY_KEY = 'storage-locations';

/**
 * Hook to fetch storage locations with filtering and pagination
 */
export function useStorageLocations(params?: Partial<StorageLocationQuery>) {
  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => storageApi.list(params),
  });
}

/**
 * Hook to fetch a single storage location
 */
export function useStorageLocation(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => storageApi.get(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a storage location
 */
export function useCreateStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStorageLocationInput) => storageApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook to update a storage location
 */
export function useUpdateStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStorageLocationInput }) =>
      storageApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete a storage location
 */
export function useDeleteStorageLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => storageApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
